import React, { FC, useState } from 'react';
import { Button, useTranslate, usePermissions } from 'react-admin';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import makeStyles from '@material-ui/core/styles/makeStyles';
import MuiTextField from '@material-ui/core/TextField';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import { HeadTransaction } from '~/types/HeadTransaction';
import { decryptVoucherCode } from '~/api/headTransactions';
import { useNotify, useTimeout } from '~/hooks';

interface Props {
  transaction: HeadTransaction;
}

const DecryptVoucherCodeAction: FC<Props> = ({ transaction }) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.headTransactions.show.details.${key}`);

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const clearCodeTimeout = useTimeout(() => {
    setCode('');
  }, 60 * 1000);

  const notify = useNotify();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setCode('');
    setReason('');
  };

  const handleDecrypt = async () => {
    setLoading(true);
    try {
      const { data } = await decryptVoucherCode(transaction.id, reason);
      clearCodeTimeout.start();
      setCode(data.voucherCode);
    } catch (error) {
      notify({ message: (error as any).message, type: 'error' });
    }
    setLoading(false);
    setReason('');
  };

  const classes = useStyles();

  if (transaction.operationType !== 'VOUCHER_REDEMPTION') return null;
  if (!permissions?.includes('headTransaction.voucherCode.decrypt')) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='decrypt-voucher-dialog-title'
        aria-describedby='decrypt-voucher-dialog-description'
      >
        <DialogTitle id='decrypt-voucher-dialog-title'>{t('decryptVoucherCode')}</DialogTitle>
        <DialogContent style={{ maxWidth: 400, paddingBottom: 4 }}>
          <DialogContentText id='decrypt-voucher-dialog-description'>
            {code ? (
              <div className={classes.codeContainer}>
                <Typography variant='h3' className={classes.code}>
                  {code}
                </Typography>
                <Alert severity='error'>
                  <div>{t('decryptVoucherCodeAttention1')}</div>
                  <div>{t('decryptVoucherCodeAttention2')}</div>
                </Alert>
              </div>
            ) : (
              <MuiTextField
                name='reason'
                label={t('voucherCodeReason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={5}
                rowsMax={20}
                style={{ minWidth: 400 }}
                required
              />
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.buttonsContainer}>
          {code ? (
            <Button label='ra.action.close' onClick={handleClose} />
          ) : (
            <>
              <Button label='ra.action.cancel' onClick={handleClose} />
              <Button
                label={translate('utils.confirmation.confirm')}
                onClick={handleDecrypt}
                variant='contained'
                disabled={reason.length < 1}
              >
                <CheckCircleIcon />
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Button label={t('decryptVoucherCode')} onClick={handleOpen} disabled={loading}>
        {loading ? <CircularProgress size={18} thickness={2} /> : <VisibilityIcon />}
      </Button>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  codeContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: 400,
    height: 109,
  },
  buttonsContainer: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
  code: {
    marginBottom: theme.spacing(3),
  },
}));

export default DecryptVoucherCodeAction;
